// Which Stores columns a viewer chose to see, saved per browser (WEB_PLAN.md §5.5 "choosable, saved per browser").
// Only explicit choices are stored, as `{ "v": 1, "columns": { "phone": true, "stage": false } }`; every other column
// follows its default. So a custom field added next month, or a default changed in a later build, still shows up as
// intended instead of being frozen by an old snapshot of the visible list.
//
// localStorage may be missing, full or blocked (private windows, strict settings) and its contents may be anything
// (an older build, a hand edit), so reading never throws and anything unexpected counts as "no choices".

import { isColumnId, type StoreColumnSpec } from './columns';

export const COLUMN_PREFS_KEY = 'gt.stores.columns';
const VERSION = 1;
/** A sane bound: base columns plus a generous number of custom fields. */
const MAX_ENTRIES = 200;

export type ColumnChoices = Readonly<Record<string, boolean>>;

/** Parses the saved value; `{}` for missing, malformed, foreign-version or oversized data. Invalid entries drop. */
export function parseColumnPrefs(raw: string | null | undefined): ColumnChoices {
  if (!raw) return {};
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return {};
  const { v, columns } = data as { v?: unknown; columns?: unknown };
  if (v !== VERSION || typeof columns !== 'object' || columns === null || Array.isArray(columns)) return {};
  const out: Record<string, boolean> = {};
  let n = 0;
  for (const [id, visible] of Object.entries(columns as Record<string, unknown>)) {
    if (!isColumnId(id) || typeof visible !== 'boolean') continue;
    // Custom field ids are case-insensitive; keep one entry per field.
    out[id.startsWith('cf:') ? id.toLowerCase() : id] = visible;
    n += 1;
    if (n >= MAX_ENTRIES) break;
  }
  return out;
}

export function serializeColumnPrefs(choices: ColumnChoices): string {
  const columns: Record<string, boolean> = {};
  for (const [id, visible] of Object.entries(choices)) if (isColumnId(id)) columns[id] = visible;
  return JSON.stringify({ v: VERSION, columns });
}

/** Whether a column shows: the viewer's choice, else its default. Name always shows. */
export function isColumnVisible(spec: StoreColumnSpec, choices: ColumnChoices): boolean {
  if (!spec.hideable) return true;
  return choices[spec.id] ?? spec.defaultVisible;
}

/** The visible columns, in table order. */
export function visibleColumns(specs: readonly StoreColumnSpec[], choices: ColumnChoices): StoreColumnSpec[] {
  return specs.filter((s) => isColumnVisible(s, choices));
}

/**
 * Records one choice. A choice equal to the default is removed rather than stored, so defaults can evolve; hiding
 * a column that cannot be hidden is ignored.
 */
export function setColumnChoice(choices: ColumnChoices, spec: StoreColumnSpec, visible: boolean): ColumnChoices {
  if (!spec.hideable) return choices;
  const next: Record<string, boolean> = { ...choices };
  if (visible === spec.defaultVisible) delete next[spec.id];
  else next[spec.id] = visible;
  return next;
}

/** Reads the saved choices (never throws). */
export function loadColumnPrefs(storage: Pick<Storage, 'getItem'> | null | undefined): ColumnChoices {
  try {
    return parseColumnPrefs(storage?.getItem(COLUMN_PREFS_KEY));
  } catch {
    return {};
  }
}

/** Saves the choices (silently does nothing when storage is unavailable or full). */
export function saveColumnPrefs(storage: Pick<Storage, 'setItem' | 'removeItem'> | null | undefined, choices: ColumnChoices): void {
  try {
    if (!storage) return;
    if (Object.keys(choices).length === 0) storage.removeItem(COLUMN_PREFS_KEY);
    else storage.setItem(COLUMN_PREFS_KEY, serializeColumnPrefs(choices));
  } catch {
    // Quota exceeded or storage blocked: the choice still applies for this page view.
  }
}
